import PropTypes from "prop-types";
import { Component } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";

// The model is downloaded and parsed inside the <Canvas>, where a failure
// throws while rendering. Without a boundary it escapes the whole app, so
// catching it here keeps the viewer and its controls up.
//
// resetKey: change it to give the model a fresh attempt.
export class ModelErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onFailed(true);
  }

  componentDidUpdate(previous) {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) {
      // when the model changed under a broken canvas (e.g., a re-issued URL,
      // a finished measurement): give it a fresh attempt, and report that it
      // is no longer failing so the viewer stops showing the error UI.
      this.setState({ failed: false });
      this.props.onFailed(false);
    }
  }

  render() {
    // the canvas stays unmounted while broken — a WebGL context that threw
    // mid-render is not worth keeping around.
    return this.state.failed ? null : this.props.children;
  }
}

ModelErrorBoundary.propTypes = {
  resetKey: PropTypes.string,
  onFailed: PropTypes.func.isRequired,
  children: PropTypes.node,
};

// The UI shown when a model could not be shown, for two cases: the model URL
// could not be re-issued (will show a fullScreen view), or the model behind a
// a valid URL could not be downloaded (shown over the blank viewer, with its
// own control panel to handle model deletion or go back).
//
// Only the offered actions are rendered.
export const ModelLoadError = ({
  headline,
  detail,
  onRetry,
  onDelete,
  onBack,
  fullScreen = false,
}) => (
  <Box
    sx={{
      position: fullScreen ? "fixed" : "absolute",
      inset: 0,
      zIndex: fullScreen ? 1400 : 10,
      backgroundColor: fullScreen ? "#fff" : "transparent",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      px: 4,
      textAlign: "center",
    }}
  >
    <Alert severity="error">{headline}</Alert>
    <Typography variant="body2" color="text.secondary">
      {detail}
    </Typography>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        width: 240,
        mt: 1,
      }}
    >
      {onRetry && (
        <Button variant="contained" onClick={() => onRetry()}>
          Try Again
        </Button>
      )}
      {onDelete && (
        <Button variant="outlined" color="error" onClick={() => onDelete()}>
          Delete &amp; Rescan
        </Button>
      )}
      {onBack && <Button onClick={() => onBack()}>Back to Dashboard</Button>}
    </Box>
  </Box>
);

ModelLoadError.propTypes = {
  headline: PropTypes.string.isRequired,
  detail: PropTypes.string.isRequired,
  onRetry: PropTypes.func,
  onDelete: PropTypes.func,
  onBack: PropTypes.func,
  fullScreen: PropTypes.bool,
};
